import React, { useEffect } from "react";
import { SnowflakeWarehouseSizeEnum, SnowflakeWarehouseSizeEnumDescription } from "@app/client";
import { ClientContext } from "@app/admin/contexts/ClientContext";
import { SnowflakeService } from "@app/snowflake/snowflakeService";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";

interface ClientSnowflakeWarehouseSettingsProps {
  disabled?: boolean;
}

export const ClientSnowflakeWarehouseSettings: React.FC<React.PropsWithChildren<ClientSnowflakeWarehouseSettingsProps>> = (props: ClientSnowflakeWarehouseSettingsProps) => {
  const clientContext = React.useContext(ClientContext);
  const snowflakeService = new SnowflakeService();

  //General purpose warehouse app settings:
  const [appMinWarehouseSize, setAppMinWarehouseSize] = React.useState<string>();
  const [appDefaultWarehouseSize, setAppDefaultWarehouseSize] = React.useState<string>();
  const [appMaxWarehouseSize, setAppMaxWarehouseSize] = React.useState<string>();

  //Background Warehouse app settings
  const [appBackgroundMinWarehouseSize, setAppBackgroundMinWarehouseSize] = React.useState<string>();
  const [appBackgroundDefaultWarehouseSize, setAppBackgroundDefaultWarehouseSize] = React.useState<string>();
  const [appBackgroundMaxWarehouseSize, setAppBackgroundMaxWarehouseSize] = React.useState<string>();

  useEffect(() => {
    snowflakeService.getAppWarehouseSizeConfiguration().then((appConfig) => {
      setAppMinWarehouseSize(appConfig.warehouseSizeMin);
      setAppDefaultWarehouseSize(appConfig.warehouseSizeDefault);
      setAppMaxWarehouseSize(appConfig.warehouseSizeMax);
      setAppBackgroundMinWarehouseSize(appConfig.backgroundWarehouseSizeMin);
      setAppBackgroundDefaultWarehouseSize(appConfig.backgroundWarehouseSizeDefault);
      setAppBackgroundMaxWarehouseSize(appConfig.backgroundWarehouseSizeMax);
    });
  }, []);
  
  return (
    <div className="snowflake-warehouse-settings">
      <div className="warehouse-settings-grid">

        {/* General Purpose Warehouse Settings Column */}
        <div className="warehouse-column">

          <h5 className="warehouse-section-title">General Purpose Warehouse Settings</h5>

          <QESSelect
            containerClassName="mb-3"
            label={`Client Minimum Warehouse Size (Application: ${SnowflakeWarehouseSizeEnumDescription[appMinWarehouseSize as SnowflakeWarehouseSizeEnum]})`}
            testId="snowflake-warehouse-settings__general-purpose-min"
            value={clientContext.snowflakeWarehouseMin}
            onChange={(e) => clientContext.setSnowflakeWarehouseMin(e.target.value as SnowflakeWarehouseSizeEnum)}
            disabled={props.disabled}
            isValid={clientContext.validationObject["snowflakeWarehouses"]}
            allowEmpty={true}
            placeholder={"Select a Warehouse Size:"}
          >
            {Object.values(SnowflakeWarehouseSizeEnum).map((opt, index) => 
              <option key={index} value={opt}>
                {SnowflakeWarehouseSizeEnumDescription[opt]}
              </option>
            )}
          </QESSelect>

          <QESSelect
            containerClassName="mb-3"
            label={`Client Default Warehouse Size (Application: ${SnowflakeWarehouseSizeEnumDescription[appDefaultWarehouseSize as SnowflakeWarehouseSizeEnum]})`}
            testId="snowflake-warehouse-settings__general-purpose-default"
            value={clientContext.snowflakeWarehouseDefault}
            onChange={(e) => clientContext.setSnowflakeWarehouseDefault(e.target.value as SnowflakeWarehouseSizeEnum)}
            disabled={props.disabled}
            isValid={clientContext.validationObject["snowflakeWarehouses"]}
            allowEmpty={true}
            placeholder={"Select a Warehouse Size:"}
          >
            {Object.values(SnowflakeWarehouseSizeEnum).map((opt, index) => 
              <option key={index} value={opt}>
                {SnowflakeWarehouseSizeEnumDescription[opt]}
              </option>
            )}
          </QESSelect>

          <QESSelect
            containerClassName="mb-3"
            label={`Client Maximum Warehouse Size (Application: ${SnowflakeWarehouseSizeEnumDescription[appMaxWarehouseSize as SnowflakeWarehouseSizeEnum]})`}
            testId="snowflake-warehouse-settings__general-purpose-max"
            value={clientContext.snowflakeWarehouseMax}
            onChange={(e) => clientContext.setSnowflakeWarehouseMax(e.target.value as SnowflakeWarehouseSizeEnum)}
            disabled={props.disabled}
            isValid={clientContext.validationObject["snowflakeWarehouses"]}
            allowEmpty={true}
            placeholder={"Select a Warehouse Size:"}
          >
            {Object.values(SnowflakeWarehouseSizeEnum).map((opt, index) => 
              <option key={index} value={opt}>
                {SnowflakeWarehouseSizeEnumDescription[opt]}
              </option>
            )}
          </QESSelect>
        </div>

        {/* Background Warehouse Settings Column */}
        <div className="warehouse-column">

          <h5 className="warehouse-section-title">Background Warehouse Settings</h5>

          <QESSelect
            containerClassName="mb-3"
            label={`Client Minimum Warehouse Size (Application: ${SnowflakeWarehouseSizeEnumDescription[appBackgroundMinWarehouseSize as SnowflakeWarehouseSizeEnum]})`}
            testId="snowflake-warehouse-settings__background-min"
            value={clientContext.snowflakeBackgroundWarehouseMin}
            onChange={(e) => clientContext.setSnowflakeBackgroundWarehouseMin(e.target.value as SnowflakeWarehouseSizeEnum)}
            disabled={props.disabled}
            isValid={clientContext.validationObject["snowflakeBackgroundWarehouses"]}
            allowEmpty={true}
            placeholder={"Select a Warehouse Size:"}
          >
            {Object.values(SnowflakeWarehouseSizeEnum).map((opt, index) => 
              <option key={index} value={opt}>
                {SnowflakeWarehouseSizeEnumDescription[opt]}
              </option>
            )}
          </QESSelect>

          <QESSelect
            containerClassName="mb-3"
            label={`Client Default Warehouse Size (Application: ${SnowflakeWarehouseSizeEnumDescription[appBackgroundDefaultWarehouseSize as SnowflakeWarehouseSizeEnum]})`}
            testId="snowflake-warehouse-settings__background-default"
            value={clientContext.snowflakeBackgroundWarehouseDefault}
            onChange={(e) => clientContext.setSnowflakeBackgroundWarehouseDefault(e.target.value as SnowflakeWarehouseSizeEnum)}
            disabled={props.disabled}
            isValid={clientContext.validationObject["snowflakeBackgroundWarehouses"]}
            allowEmpty={true}
            placeholder={"Select a Warehouse Size:"}
          >
            {Object.values(SnowflakeWarehouseSizeEnum).map((opt, index) => 
              <option key={index} value={opt}>
                {SnowflakeWarehouseSizeEnumDescription[opt]}
              </option>
            )}
          </QESSelect>

          <QESSelect
            containerClassName="mb-3"
            label={`Client Maximum Warehouse Size (Application: ${SnowflakeWarehouseSizeEnumDescription[appBackgroundMaxWarehouseSize as SnowflakeWarehouseSizeEnum]})`}
            testId="snowflake-warehouse-settings__background-max"
            value={clientContext.snowflakeBackgroundWarehouseMax}
            onChange={(e) => clientContext.setSnowflakeBackgroundWarehouseMax(e.target.value as SnowflakeWarehouseSizeEnum)}
            disabled={props.disabled}
            isValid={clientContext.validationObject["snowflakeBackgroundWarehouses"]}
            allowEmpty={true}
            placeholder={"Select a Warehouse Size:"}
          >
            {Object.values(SnowflakeWarehouseSizeEnum).map((opt, index) => 
              <option key={index} value={opt}>
                {SnowflakeWarehouseSizeEnumDescription[opt]}
              </option>
            )}
          </QESSelect>

        </div>

      </div>
    </div>
  );
}